import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Briefcase, Users, Calendar, Star, Phone, Mail, CheckCircle, Crown } from 'lucide-react';

const PrivateChauffeurBrussels = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Private Chauffeur Brussels | Luxury Driver Service Belgium 24/7';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Hire a private chauffeur in Brussels. Professional drivers, luxury Mercedes & BMW vehicles. Business meetings, events, city tours. Book your Brussels chauffeur 24/7.');
    }
  }, []);

  const scrollToBooking = () => {
    navigate('/');
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
      {/* Hero */}
      <section className="relative py-24 px-6 text-center bg-gradient-to-r from-black via-gray-900 to-black">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-gold mb-6">
            Private Chauffeur Brussels
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8">
            Luxury Driver Service | Business & VIP Transport Belgium
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={scrollToBooking}
              className="bg-gold hover:bg-amber-600 text-black text-lg px-8 py-6"
            >
              <Crown className="mr-2" /> Hire Private Chauffeur
            </Button>
            <Button 
              onClick={() => window.location.href = 'tel:+3223150254'}
              variant="outline"
              className="border-gold text-gold hover:bg-gold/20 text-lg px-8 py-6"
            >
              <Phone className="mr-2" /> +32 2 315 02 54
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-bold text-gold mb-6">
              Professional Private Chauffeur Service Brussels
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-4">
              Luxury Transfer Services offers premium <strong>private chauffeur service in Brussels</strong> for business executives, VIP guests, and discerning travelers. Our professional drivers provide discreet, reliable, and luxury transport throughout Belgium.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-4">
              Whether you need a <strong>Brussels private driver</strong> for business meetings, corporate events, or private city tours, our fleet of luxury Mercedes and BMW vehicles ensures first-class travel experience.
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <span><strong>Hourly hire</strong> – From 3 hours (€75/hour)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <span><strong>Full day service</strong> – 8+ hours available</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <span><strong>Multi-city transfers</strong> – Brussels, Bruges, Antwerp, Ghent</span>
              </li>
            </ul>
          </div>

          <Card className="bg-gray-800/50 border-gold/30">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gold mb-6">Why Hire Our Private Chauffeur?</h3>
              <ul className="space-y-4">
                {[
                  'Experienced professional drivers',
                  'English, French & Dutch speaking',
                  'Luxury Mercedes E-Class & BMW 5 Series',
                  'Discreet & confidential service',
                  'Business-class comfort',
                  'Flexible hourly or daily rates',
                  'Wait time included',
                  'Meet & greet service'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-300">
                    <Star className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Services */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gold mb-8 text-center">
            Private Chauffeur Services in Brussels
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gray-800/50 border-gold/30 hover:border-gold transition-all">
              <CardContent className="p-6 text-center">
                <Briefcase className="w-12 h-12 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">Business Meetings</h3>
                <p className="text-gray-400 mb-4">
                  Executive transport for corporate meetings, conferences, and business events in Brussels.
                </p>
                <p className="text-gold font-semibold">From €75/hour</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30 hover:border-gold transition-all">
              <CardContent className="p-6 text-center">
                <Users className="w-12 h-12 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">VIP Events</h3>
                <p className="text-gray-400 mb-4">
                  Premium chauffeur service for galas, weddings, corporate events, and special occasions.
                </p>
                <p className="text-gold font-semibold">From €60/hour</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30 hover:border-gold transition-all">
              <CardContent className="p-6 text-center">
                <Calendar className="w-12 h-12 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">City Tours</h3>
                <p className="text-gray-400 mb-4">
                  Private Brussels city tours with professional driver-guide. Discover Belgium in comfort.
                </p>
                <p className="text-gold font-semibold">Half-day / Full-day</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Ideal For */}
        <div className="mb-16 bg-gray-800/50 border border-gold/30 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gold mb-8 text-center">Ideal For</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Business Travelers</h3>
              <ul className="space-y-2 text-gray-300">
                <li>✓ Executive board meetings</li>
                <li>✓ Conference transport</li>
                <li>✓ EU Parliament & Commission visits</li>
                <li>✓ Multi-city business trips</li>
                <li>✓ Client meetings & presentations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-4">VIP & Leisure</h3>
              <ul className="space-y-2 text-gray-300">
                <li>✓ Private city tours</li>
                <li>✓ Shopping trips</li>
                <li>✓ Restaurant reservations transport</li>
                <li>✓ Theatre & concert events</li>
                <li>✓ Special celebrations</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gold mb-8 text-center">Private Chauffeur Rates Brussels</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gray-800/50 border-gold/30">
              <CardContent className="p-6 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">Hourly Service</h3>
                <div className="text-4xl font-bold text-gold my-4">€75/h</div>
                <p className="text-gray-400 mb-4">Minimum 3 hours</p>
                <ul className="text-left space-y-2 text-gray-300 text-sm">
                  <li>✓ Flexible schedule</li>
                  <li>✓ Multiple stops included</li>
                  <li>✓ Wait time included</li>
                  <li>✓ Luxury Mercedes/BMW</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30 ring-2 ring-gold">
              <CardContent className="p-6 text-center">
                <div className="bg-gold text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">POPULAR</div>
                <h3 className="text-2xl font-bold text-white mb-2">Half Day (4h)</h3>
                <div className="text-4xl font-bold text-gold my-4">€280</div>
                <p className="text-gray-400 mb-4">4 hours included</p>
                <ul className="text-left space-y-2 text-gray-300 text-sm">
                  <li>✓ Best value</li>
                  <li>✓ Business meetings</li>
                  <li>✓ City tours</li>
                  <li>✓ Premium service</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 border-gold/30">
              <CardContent className="p-6 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">Full Day (8h)</h3>
                <div className="text-4xl font-bold text-gold my-4">€520</div>
                <p className="text-gray-400 mb-4">8 hours included</p>
                <ul className="text-left space-y-2 text-gray-300 text-sm">
                  <li>✓ All-day availability</li>
                  <li>✓ Long distance trips</li>
                  <li>✓ Extended events</li>
                  <li>✓ VIP treatment</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-gold/20 to-amber-600/20 border border-gold/50 rounded-lg p-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Book Your Private Chauffeur in Brussels
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Professional luxury driver service | Available 24/7 | From €75/hour
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={scrollToBooking}
              className="bg-gold hover:bg-amber-600 text-black text-lg px-8 py-6"
            >
              <Crown className="mr-2" /> Hire Now
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-gold text-gold hover:bg-gold/20 text-lg px-8 py-6"
            >
              View All Services
            </Button>
          </div>
          <div className="mt-8 flex items-center justify-center gap-8 text-gray-300">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-gold" />
              <span>+32 2 315 02 54</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-gold" />
              <span>contact@luxurytransferservices.be</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivateChauffeurBrussels;
